import { useMemo, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import { Box, Button, Chip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import {
  type ExperimentalAppointment,
  getFixtureProvider,
  getFixtureWaitlistCandidates,
} from '../../fixtures';
import { getColorForId, providerInitials } from '../../theme/extendedPalette';
import type { WaitlistEntry } from '../../types/waitlist';

interface Props {
  appointment: ExperimentalAppointment;
  /** Reason this surface is appearing — controls the lead-in copy. */
  reason: 'cancelled' | 'no_show' | 'rescheduling';
}

/**
 * Snowplow surface (GDN-1214). When the slot is open — appointment cancelled,
 * no-show, or pending reschedule — show "N patients on the waitlist could
 * take this slot" with the candidates filtered by the appointment's provider.
 *
 * Reads in-memory fixtures via getFixtureWaitlistCandidates(provider_id);
 * production wires this to `GET /v2/provider/waitlist/candidates`.
 */
export const WaitlistCandidatesPanel = ({ appointment, reason }: Props) => {
  const providerId = appointment.provider_id ?? null;
  const providerName = getFixtureProvider(providerId)?.name ?? 'Unassigned';

  const candidates = useMemo(
    () => getFixtureWaitlistCandidates(providerId),
    [providerId],
  );

  // Track which candidates have been "offered" — purely demo state, no
  // persistence. Mirrors the eventual BE flow where offering an entry would
  // record an OFFERED status (deferred per the ticket — v1 just shows the
  // list; offering is a future enhancement).
  const [offeredIds, setOfferedIds] = useState<Set<string>>(new Set());
  const offerCount = offeredIds.size;

  if (candidates.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px dashed ${ELIG_TOKENS.line}`,
          bgcolor: ELIG_TOKENS.bg,
        }}
      >
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
          No waitlist candidates match this slot.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${ELIG_TOKENS.line}`,
        bgcolor: ELIG_TOKENS.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${ELIG_TOKENS.line}`,
          bgcolor: 'rgba(28, 30, 133, 0.04)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <EventAvailableOutlined sx={{ fontSize: 18, color: ELIG_TOKENS.midnight }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: ELIG_TOKENS.midnight }}>
            {leadIn(reason, candidates.length)}
          </Typography>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
            Filtered to {providerName}
            {providerId ? ' · plus anyone-OK' : ''}
          </Typography>
        </Box>
        {offerCount > 0 && (
          <Chip
            size="small"
            label={`${offerCount} offered`}
            sx={{
              bgcolor: ELIG_TOKENS.green,
              color: '#fff',
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      <Box>
        {candidates.map((c) => (
          <CandidateRow
            key={c.id}
            entry={c}
            offered={offeredIds.has(c.id)}
            onToggleOffer={() =>
              setOfferedIds((prev) => {
                const next = new Set(prev);
                if (next.has(c.id)) next.delete(c.id);
                else next.add(c.id);
                return next;
              })
            }
          />
        ))}
      </Box>
    </Box>
  );
};

const leadIn = (reason: Props['reason'], n: number): string => {
  const noun = n === 1 ? 'patient' : 'patients';
  switch (reason) {
    case 'cancelled':
      return `${n} ${noun} on the waitlist could take this slot`;
    case 'no_show':
      return `Slot freed up — ${n} ${noun} on the waitlist could fill it`;
    case 'rescheduling':
      return `If this reschedules, ${n} ${noun} on the waitlist could take the original slot`;
  }
};

const CandidateRow = ({
  entry,
  offered,
  onToggleOffer,
}: {
  entry: WaitlistEntry;
  offered: boolean;
  onToggleOffer: () => void;
}) => {
  const fullName = `${entry.patient.first_name} ${entry.patient.last_name}`;
  const provider = getFixtureProvider(entry.preferred_user_id);
  const preferenceColor = entry.preferred_user_id
    ? provider?.color
      ? undefined
      : getColorForId(entry.preferred_user_id)
    : ELIG_TOKENS.inkFaint;
  const waitedFor = formatDistanceToNow(new Date(entry.added_at), { addSuffix: false });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${ELIG_TOKENS.line}`,
        '&:first-of-type': { borderTop: 'none' },
        bgcolor: offered ? 'rgba(4, 120, 85, 0.04)' : 'transparent',
        transition: 'background-color 0.15s',
      }}
    >
      <Box sx={{ flexShrink: 0, mt: 0.25 }}>
        <PatientAvatar id={entry.patient_id} name={fullName} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: ELIG_TOKENS.ink }}
          >
            {fullName}
          </Typography>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint, flexShrink: 0 }}>
            Waiting {waitedFor}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <PersonOutlined sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint }} />
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
            {provider ? `Prefers ${provider.name}` : 'No clinician preference'}
          </Typography>
          {preferenceColor && entry.preferred_user_id && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: preferenceColor,
                ml: 0.25,
              }}
            />
          )}
        </Box>

        {entry.notes && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              fontSize: 12.5,
              color: ELIG_TOKENS.inkSoft,
              fontStyle: 'italic',
              lineHeight: 1.4,
            }}
          >
            {entry.notes}
          </Typography>
        )}
      </Box>

      <Button
        size="small"
        variant={offered ? 'contained' : 'outlined'}
        onClick={onToggleOffer}
        sx={{
          flexShrink: 0,
          minWidth: 88,
          textTransform: 'none',
          fontSize: 12,
          ...(offered
            ? {
                bgcolor: ELIG_TOKENS.green,
                color: '#fff',
                '&:hover': { bgcolor: ELIG_TOKENS.green, filter: 'brightness(0.9)' },
              }
            : {
                borderColor: ELIG_TOKENS.lineStrong,
                color: ELIG_TOKENS.midnight,
                '&:hover': {
                  borderColor: ELIG_TOKENS.midnight,
                  bgcolor: 'rgba(28, 30, 133, 0.04)',
                },
              }),
        }}
      >
        {offered ? 'Offered' : 'Offer slot'}
      </Button>
    </Box>
  );
};

const PatientAvatar = ({ id, name }: { id: string; name: string }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      bgcolor: getColorForId(id),
      color: '#fff',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 700,
    }}
  >
    {providerInitials(name)}
  </Box>
);

// Re-export the empty-slot icon next to the panel so callers can render a
// matching glyph in their own headers.
export { EventBusyOutlined as SlotOpenIcon };
